import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FaTrash, FaFilePdf } from "react-icons/fa";
import { PageHeader } from "../../component/Header";
import { Button, Select, Space, Table } from "antd";
import styled from "styled-components";

const evalColor = (ev) =>
  ev === "양호"
    ? "#2e9e4f"
    : ev === "주의"
    ? "#e8920c"
    : ev === "위험"
    ? "#e0352b"
    : "inherit";

// 영역별 점수 → 등급 (Analysis.js의 임계값과 동일)
//   언어 이해 및 표현(A): 양호 ≥10, 주의 ≥7, 그 외 위험
//   말 및 문해(B):        양호 ≥14, 주의 ≥12, 그 외 위험
const evalCategoryA = (s) =>
  s == null ? "-" : s >= 10 ? "양호" : s >= 7 ? "주의" : "위험";
const evalCategoryB = (s) =>
  s == null ? "-" : s >= 14 ? "양호" : s >= 12 ? "주의" : "위험";

// 저장 시점에 기록된 등급(analysis_result)이 있으면 우선 사용해 보고서와 100% 일치시키고,
// 없으면(예외적인 옛 기록) 점수로 재계산. analysis_result는 JSON 컬럼이라 객체/문자열 모두 대응.
const readResult = (record) => {
  const r = record.analysis_result;
  if (!r) return {};
  if (typeof r === "string") {
    try {
      return JSON.parse(r);
    } catch {
      return {};
    }
  }
  return r;
};

// "6점 (위험)"을 한 줄로 표시 (등급은 색상). 행 높이를 늘리지 않기 위해 1줄 고정.
// 점수는 오른쪽 정렬 고정폭(2자리 기준)으로 둬서 한 자리/두 자리 점수의
// "점"·괄호 위치가 행마다 어긋나지 않게 정렬한다.
const ScoreWithEval = ({ score, evaluation }) => (
  <span style={{ whiteSpace: "nowrap" }}>
    <span
      style={{ display: "inline-block", minWidth: "2ch", textAlign: "right" }}
    >
      {score ?? 0}
    </span>
    점{" "}
    <span style={{ color: evalColor(evaluation), fontWeight: 600 }}>
      ({evaluation})
    </span>
  </span>
);

function History({ user }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date
      .toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      .replace(/\. /g, "-")
      .replace(/\.$/, "");
  };

  const fetchHistory = useCallback(async () => {
    if (!user || !user.id) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/sleHistory`,
        {
          params: { user_id: user.id },
          withCredentials: true,
        }
      );
      setHistory(response.data);
    } catch (error) {
      console.error("SLE 기록 불러오기 오류:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const handleDelete = async (id) => {
    if (!window.confirm("정말로 삭제하시겠습니까?")) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/api/sleHistory/${id}`,
        { withCredentials: true }
      );
      alert("삭제되었습니다.");
      fetchHistory();
    } catch (error) {
      console.error("삭제 오류:", error);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleDownloadPDF = async (record) => {
    if (!record.pdf_path) {
      alert("PDF 파일이 존재하지 않습니다.");
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/downloadSLEPDF/${record.id}`,
        { responseType: "blob", withCredentials: true }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${record.client_name}_결과보고서.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("PDF 다운로드 오류:", error);
      alert("PDF 다운로드에 실패했습니다.");
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (loading) {
    return <p>로딩 중...</p>;
  }

  return (
    <PageContainer>
      <PageHeader title="분석 기록 (체크리스트)">
        <Select
          defaultValue="10"
          onChange={(value) => setPageSize(Number(value))}
          style={{ width: 120 }}
        >
          <Select.Option value="10">10개씩 보기</Select.Option>
          <Select.Option value="20">20개씩 보기</Select.Option>
          <Select.Option value="30">30개씩 보기</Select.Option>
          <Select.Option value="50">50개씩 보기</Select.Option>
        </Select>
      </PageHeader>
      <TableContainer>
        <Table
          scroll={{ x: "max-content" }}
          dataSource={history}
          rowKey="id"
          columns={[
            { title: "이름", dataIndex: "client_name", key: "client_name" },
            {
              title: "성별",
              dataIndex: "client_gender",
              key: "client_gender",
            },
            {
              title: "생년월일",
              dataIndex: "client_birthday",
              key: "client_birthday",
              render: (text) => formatDate(text),
            },
            {
              title: "분석일",
              dataIndex: "analysis_date",
              key: "analysis_date",
              render: (text) => formatDate(text),
            },
            {
              title: "총점",
              key: "total_score",
              render: (_, r) =>
                (r.category_a_score ?? 0) + (r.category_b_score ?? 0),
            },
            {
              title: "판정",
              dataIndex: "total_evaluation",
              key: "total_evaluation",
              render: (text) => (
                <span style={{ color: evalColor(text), fontWeight: 600 }}>
                  {text || "-"}
                </span>
              ),
            },
            {
              title: "언어 이해 및 표현",
              key: "category_a",
              render: (_, r) => (
                <ScoreWithEval
                  score={r.category_a_score}
                  evaluation={
                    readResult(r).categoryAEvaluation ||
                    evalCategoryA(r.category_a_score)
                  }
                />
              ),
            },
            {
              title: "말 및 문해",
              key: "category_b",
              render: (_, r) => (
                <ScoreWithEval
                  score={r.category_b_score}
                  evaluation={
                    readResult(r).categoryBEvaluation ||
                    evalCategoryB(r.category_b_score)
                  }
                />
              ),
            },
            {
              title: "보고서",
              dataIndex: "pdf_path",
              key: "pdf_path",
              render: (text, record) =>
                record.pdf_path ? (
                  <Space
                    style={{ cursor: "pointer" }}
                    onClick={() => handleDownloadPDF(record)}
                  >
                    <FaFilePdf color="var(--text-secondary)" />
                    <span>{`${record.client_name}_결과보고서.pdf`}</span>
                  </Space>
                ) : (
                  <span style={{ color: "var(--text-secondary)" }}>
                    파일이 없습니다.
                  </span>
                ),
            },
            {
              title: "삭제",
              dataIndex: "id",
              key: "delete",
              render: (_, record) => (
                <Button
                  type="link"
                  danger
                  style={{ padding: 0 }}
                  onClick={() => handleDelete(record.id)}
                >
                  <FaTrash />
                </Button>
              ),
            },
          ]}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: false,
            style: { justifyContent: "center" },
          }}
        />
      </TableContainer>
    </PageContainer>
  );
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--bg-body);
  max-width: 1280px;
  margin: 0 auto;
  gap: 16px;
`;

const TableContainer = styled.div`
  padding: 20px;
  background-color: var(--bg-body);
  box-sizing: border-box;
`;

export default History;
